import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BookOpen, CalendarPlus, Camera, Check, ImagePlus, Loader2, PenLine, Plus, RotateCcw, Search, Trash2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { ApiService } from "../../services/api";
import type { BookCatalogItem, BookDaily, BookErrorType, BookPayload, BookReviewItem, BookReviews, BookReadQuota, BookResultBody, BookSource, BookTestDraft, StudentBook, StudentBookResult, StudentBookSummary, StudentBookTest, StudentBookWrong } from "../../types/navigator";

// Öğrencinin kâğıt kitapları. Ücretsizdir; plan ve hedef Koçum'un ödeme kapısında kalır.
// Fotoğraftan yalnız test listesi çıkar: kitabın sorusu ve cevap anahtarı sisteme girmez.

const MAX_FOTO = 4;
const MAX_FOTO_MB = 12;

const KAYNAK_ADLARI: Record<BookSource, string> = { kendi: "Kendim aldım", okul: "Okul", koc: "Koçum", kurs: "Kurs" };

// Genişlik ayrı: tek sınıfta "w-full" ile "w-20" yarışır ve küçük kutu bütün satırı kaplar
const INPUT_TABAN = "rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";
const INPUT = `w-full ${INPUT_TABAN}`;
const BTN_ANA = "inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50";
const BTN_IKINCIL = "inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50";

const MAX_SORU = 500;
const MAX_DAKIKA = 600;

// Okunan liste öğrenciye bir hak harcattı: sekme değişince ya da sayfa yenilenince kaybolmasın
const TASLAK_ANAHTARI = "kitaplarim-taslak";

const taslakOku = (): BookPayload | null => {
  try {
    const kayit = sessionStorage.getItem(TASLAK_ANAHTARI);
    return kayit ? JSON.parse(kayit) : null;
  } catch {
    return null;
  }
};

const taslakYaz = (taslak: BookPayload | null) => {
  try {
    if (taslak) sessionStorage.setItem(TASLAK_ANAHTARI, JSON.stringify(taslak));
    else sessionStorage.removeItem(TASLAK_ANAHTARI);
  } catch {
    /* taslak kolaylıktır, yazılamazsa akış sürer */
  }
};

const bugunAnahtari = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });

const gunYazisi = (gun: string) => new Date(`${gun}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });

const hataMetni = (error: unknown) => (error instanceof Error ? error.message : "Bir sorun oluştu, yeniden dene.");

const sayiYaDaBos = (value: string) => {
  const n = Number(value);
  return value.trim() && Number.isInteger(n) && n > 0 && n <= MAX_SORU ? n : null;
};

function Ilerleme({ book }: { book: StudentBookSummary }) {
  return (
    <div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-blue-600" style={{ width: `${book.percent}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-gray-600">
        {book.solvedTests} / {book.testCount} test
        {book.solvedQuestions > 0 && ` · ${book.solvedQuestions.toLocaleString("tr-TR")}${book.questionCount ? ` / ${book.questionCount.toLocaleString("tr-TR")}` : ""} soru`}
        {book.successPercent !== null && ` · %${book.successPercent} doğru`}
      </p>
      {book.estimatedFinish && <p className="mt-1 text-xs text-teal-700">Bu hızla {gunYazisi(book.estimatedFinish)} tarihinde biter</p>}
    </div>
  );
}

const FILTRE_ESIGI = 4;
const DIGER = "Diğer";

function Secim({ etiket, secenekler, secili, onSec, ad }: { etiket: string; secenekler: string[]; secili: string; onSec: (deger: string) => void; ad: (deger: string) => string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-12 shrink-0 text-xs font-medium text-gray-500">{etiket}</span>
      {["", ...secenekler].map((deger) => (
        <button key={deger || "hepsi"} type="button" aria-pressed={secili === deger} onClick={() => onSec(deger)} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${secili === deger ? "border-blue-600 bg-blue-50 text-blue-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
          {deger ? ad(deger) : "Hepsi"}
        </button>
      ))}
    </div>
  );
}

function KitapListesi({ books, daily, tekrar, onTekrar, onOpen, onAdd }: { books: StudentBookSummary[]; daily: BookDaily[]; tekrar: BookReviews | null; onTekrar: () => void; onOpen: (id: string) => void; onAdd: () => void }) {
  const [ders, setDers] = useState("");
  const [kaynak, setKaynak] = useState("");
  const [baslanan, setBaslanan] = useState(false);

  const dersOf = (book: StudentBookSummary) => book.subject || DIGER;
  const dersler = useMemo(() => Array.from(new Set(books.map(dersOf))).sort((a, b) => a.localeCompare(b, "tr")), [books]);
  const kaynaklar = useMemo(() => Array.from(new Set(books.map((book) => book.source))), [books]);
  const gorunen = books.filter((book) => (!ders || dersOf(book) === ders) && (!kaynak || book.source === kaynak) && (!baslanan || book.solvedTests > 0));

  const toplamCozulen = books.reduce((a, book) => a + book.solvedQuestions, 0);
  const bilinenToplam = books.reduce((a, book) => a + (book.questionCount || 0), 0);
  const eksikSayili = books.filter((book) => book.questionCount === null).length;
  const bugun = daily[daily.length - 1]?.questions || 0;
  const dun = daily[daily.length - 2]?.questions || 0;
  const hafta = daily.reduce((a, gun) => a + gun.questions, 0);
  const sayi = (n: number) => n.toLocaleString("tr-TR");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Kitaplarım</h2>
          {!books.length && <p className="text-sm text-gray-500">Kâğıt kitaplarındaki ilerlemeni burada tut.</p>}
        </div>
        <button type="button" onClick={onAdd} className={`${BTN_ANA} shrink-0`}>
          <Plus className="h-4 w-4" /> Kitap ekle
        </button>
      </div>

      {tekrar && tekrar.items.length > 0 && (
        <button type="button" onClick={onTekrar} className="flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-left hover:border-amber-400">
          <RotateCcw className="h-5 w-5 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">Bugün {tekrar.items.length} yanlış sorunu tekrar et</span>
        </button>
      )}

      {books.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-8 text-center">
          <BookOpen className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm font-medium text-gray-900">Henüz kitap eklemedin</p>
          <p className="mt-1 text-sm text-gray-500">İçindekiler sayfasının fotoğrafını çek, testlerin gelsin. Çözdükçe doğru ve yanlış sayını gir.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[["Kitap", sayi(books.length)], ["Toplam soru", sayi(bilinenToplam)], ["Çözülen", sayi(toplamCozulen)], ["Bekleyen", sayi(Math.max(0, bilinenToplam - toplamCozulen))]].map(([ad, deger]) => (
              <div key={ad} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500">{ad}</p>
                <p className="text-xl font-semibold text-gray-900">{deger}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600">Bugün {sayi(bugun)} soru · Dün {sayi(dun)} soru · Son 7 gün {sayi(hafta)} soru</p>
          {eksikSayili > 0 && <p className="text-sm text-amber-700">{eksikSayili} kitapta soru sayısı eksik, toplam ve bekleyen eksik görünür. Kitabı açıp "Testleri düzenle"den tamamlayabilirsin.</p>}

          {books.length >= FILTRE_ESIGI && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
              {dersler.length > 1 && <Secim etiket="Ders" secenekler={dersler} secili={ders} onSec={setDers} ad={(deger) => deger} />}
              {kaynaklar.length > 1 && <Secim etiket="Kaynak" secenekler={kaynaklar} secili={kaynak} onSec={setKaynak} ad={(deger) => KAYNAK_ADLARI[deger as BookSource]} />}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input id="kitap-baslanan" type="checkbox" checked={baslanan} onChange={(e) => setBaslanan(e.target.checked)} /> Sadece başladıklarım
              </label>
            </div>
          )}

          {gorunen.length === 0 && <p className="text-sm text-gray-500">Bu seçime uyan kitap yok.</p>}
          {dersler.filter((ad) => gorunen.some((book) => dersOf(book) === ad)).map((ad) => {
            const liste = gorunen.filter((book) => dersOf(book) === ad);
            const cozulen = liste.reduce((a, book) => a + book.solvedQuestions, 0);
            return (
              <section key={ad} className="space-y-2">
                {dersler.length > 1 && (
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <h3 className="text-sm font-semibold text-gray-900">{ad}</h3>
                    <p className="text-xs text-gray-500">{liste.length} kitap · {sayi(cozulen)} soru çözüldü</p>
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  {liste.map((book) => (
                    <button key={book.id} type="button" onClick={() => onOpen(book.id)} className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-blue-300">
                      <p className="truncate font-semibold text-gray-900">{book.title}</p>
                      <p className="mb-3 truncate text-xs text-gray-500">{[book.publisher, KAYNAK_ADLARI[book.source]].filter(Boolean).join(" · ")}</p>
                      <Ilerleme book={book} />
                    </button>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

// Aynı kitabı daha önce biri eklediyse test listesi hazır gelir, fotoğraf çekilmez ve okuma hakkı harcanmaz.
function KitapArama({ kapali, onSec }: { kapali: boolean; onSec: (taslak: BookPayload) => void }) {
  const [aranan, setAranan] = useState("");
  const [sonuc, setSonuc] = useState<BookCatalogItem[]>([]);
  const [durum, setDurum] = useState<"bos" | "araniyor" | "bitti" | "hata">("bos");
  const [hata, setHata] = useState("");

  useEffect(() => {
    if (aranan.trim().length < 2) {
      setSonuc([]);
      setDurum("bos");
      return;
    }
    let alive = true;
    setDurum("araniyor");
    const zaman = setTimeout(async () => {
      try {
        const data = await ApiService.searchBookCatalog(aranan.trim());
        if (alive) {
          setSonuc(data.items);
          setDurum("bitti");
        }
      } catch {
        if (alive) {
          setSonuc([]);
          setDurum("hata");
        }
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(zaman);
    };
  }, [aranan]);

  const sec = async (item: BookCatalogItem) => {
    setHata("");
    try {
      onSec({ ...(await ApiService.getBookCatalog(item.id)), catalogId: item.id });
    } catch (err) {
      setHata(hataMetni(err));
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-4">
      <label htmlFor="kitap-ara" className="text-sm font-medium text-gray-700">Kitabını ara</label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input id="kitap-ara" disabled={kapali} value={aranan} onChange={(e) => setAranan(e.target.value)} placeholder="Kitap adı ya da yayınevi" className={`${INPUT} pl-9`} />
      </div>
      {sonuc.length > 0 && (
        <ul className="space-y-1.5">
          {sonuc.map((item) => (
            <li key={item.id}>
              <button type="button" disabled={kapali} onClick={() => sec(item)} className="w-full rounded-lg border border-gray-200 p-2.5 text-left hover:border-blue-300 disabled:opacity-50">
                <span className="block text-sm font-medium text-gray-900">{item.title}</span>
                <span className="block text-xs text-gray-500">{[item.publisher, item.subject, `${item.testCount} test`].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {durum === "bitti" && !sonuc.length && <p className="text-xs text-gray-500">Listede bulamadık, aşağıdan fotoğrafla ekle.</p>}
      {durum === "hata" && <p className="text-xs text-rose-700">Arama şu an çalışmıyor, biraz sonra yeniden dene.</p>}
      {hata && <p className="text-sm text-rose-700">{hata}</p>}
    </div>
  );
}

function FotoAdimi({ quota, onRead, onDone, onManual, onCancel }: { quota: BookReadQuota | null; onRead: (result: BookPayload) => void; onDone: () => void; onManual: () => void; onCancel: () => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const previews = useMemo(() => files.map((file) => URL.createObjectURL(file)), [files]);
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews]);
  const acik = useRef(true);
  useEffect(() => {
    acik.current = true;
    return () => {
      acik.current = false;
    };
  }, []);

  const ekle = (list: FileList | null) => {
    if (!list || !list.length) return;
    const tumu = Array.from(list);
    if (tumu.some((file) => /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name))) {
      setError("HEIC biçimi açılamıyor. JPEG olarak yükle ya da doğrudan kamerayla çek.");
      return;
    }
    const secilen = tumu.filter((file) => file.type.startsWith("image/"));
    if (secilen.some((file) => file.size > MAX_FOTO_MB * 1024 * 1024)) {
      setError(`Bir fotoğraf en çok ${MAX_FOTO_MB} MB olabilir.`);
      return;
    }
    const yeni = [...files, ...secilen];
    if (secilen.length < tumu.length) setError("Yalnız fotoğraf yükleyebilirsin.");
    else setError(yeni.length > MAX_FOTO ? `En çok ${MAX_FOTO} fotoğraf ekleyebilirsin.` : "");
    setFiles(yeni.slice(0, MAX_FOTO));
  };

  const oku = async () => {
    setReading(true);
    setError("");
    try {
      const { title, publisher, subject, tests, readId } = await ApiService.readBookContents(files);
      if (!acik.current) return;
      if (!tests.length) {
        setError("Bu fotoğrafta test bulamadım. Sayfayı düz ve net çekip yeniden dene ya da elle ekle.");
        return;
      }
      onRead({ title, publisher, subject, tests, readId });
    } catch (err) {
      if (acik.current) setError(hataMetni(err));
    } finally {
      if (acik.current) setReading(false);
      onDone();
    }
  };

  const kalan = quota?.remaining ?? 1;
  return (
    <div className="space-y-4">
      <AdimBasligi baslik="Kitap ekle" onBack={onCancel} />
      <KitapArama kapali={reading} onSec={onRead} />
      <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <p className="text-sm text-gray-700">İçindekiler sayfasının fotoğrafını çek. Birden çok sayfa varsa en çok {MAX_FOTO} fotoğraf ekleyebilirsin. Fotoğraf okunduktan sonra saklanmaz.</p>
        <div className="grid grid-cols-2 gap-2">
          <label htmlFor="kitap-kamera" className={`${BTN_IKINCIL} cursor-pointer`}><Camera className="h-4 w-4" /> Fotoğraf çek</label>
          <label htmlFor="kitap-galeri" className={`${BTN_IKINCIL} cursor-pointer`}><ImagePlus className="h-4 w-4" /> Galeriden seç</label>
        </div>
        <input id="kitap-kamera" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { ekle(e.target.files); e.target.value = ""; }} />
        <input id="kitap-galeri" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { ekle(e.target.files); e.target.value = ""; }} />

        {files.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {previews.map((url, index) => (
              <div key={url} className="relative">
                <img src={url} alt={`${index + 1}. fotoğraf`} className="aspect-[3/4] w-full rounded-md border border-gray-200 object-cover" />
                <button type="button" aria-label={`${index + 1}. fotoğrafı kaldır`} onClick={() => setFiles((current) => current.filter((_, i) => i !== index))} className="absolute right-1 top-1 rounded-full bg-gray-900/70 p-0.5 text-white">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-rose-700">{error}</p>}
        {kalan === 0 && <p className="text-sm text-amber-700">Bugünkü okuma hakkın doldu. Yarın devam edebilir ya da testleri elle ekleyebilirsin.</p>}

        <button type="button" onClick={oku} disabled={!files.length || reading || kalan === 0} className={`${BTN_ANA} w-full`}>
          {reading ? <><Loader2 className="h-4 w-4 animate-spin" /> Testler okunuyor</> : "Testleri oku"}
        </button>
        {quota && <p className="text-center text-xs text-gray-500">Bugün kalan okuma: {quota.remaining} / {quota.limit}</p>}
      </div>
      <button type="button" onClick={onManual} className="w-full text-sm font-medium text-blue-600 hover:underline">Fotoğrafsız, elle ekle</button>
    </div>
  );
}

function AdimBasligi({ baslik, onBack }: { baslik: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={onBack} aria-label="Geri" className="rounded-full p-1.5 text-gray-600 hover:bg-gray-100"><ArrowLeft className="h-5 w-5" /></button>
      <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900">{baslik}</h2>
    </div>
  );
}

// Testler kitaptaki sırayla durur: konu değişince başlık çıkar, uzun liste taranabilir olur
const konuBasligi = (tests: BookTestDraft[], index: number) => {
  const konu = tests[index].topic;
  return konu && konu !== tests[index - 1]?.topic ? konu : null;
};

// Yan yana duran ve aynı konuyu taşıyan testler bir bölüm sayılır
const ayniBolum = (a: BookTestDraft, b: BookTestDraft) => (a.topic || "") === (b.topic || "");

function BolumBasligi({ ad, odakla, onDegis }: { ad: string; odakla: boolean; onDegis: (ad: string) => void }) {
  // Kutudan çıkınca işlenir: yazarken ad yandaki bölümle aynı olursa iki bölüm birleşip kutu kaybolmasın
  const [deger, setDeger] = useState(ad);
  return (
    <input aria-label="Bölüm adı" placeholder="Bölüm adı (isteğe bağlı)" autoFocus={odakla} maxLength={200} value={deger} onFocus={odakla ? (e) => e.target.select() : undefined} onChange={(e) => setDeger(e.target.value)} onBlur={() => onDegis(deger.trim())} className={`${INPUT} font-semibold`} />
  );
}

function KitapFormu({ initial, onSave, onCancel, baslik }: { initial: BookPayload; onSave: (payload: BookPayload) => Promise<void>; onCancel: () => void; baslik: string }) {
  const [title, setTitle] = useState(initial.title || "");
  const [publisher, setPublisher] = useState(initial.publisher || "");
  const [subject, setSubject] = useState(initial.subject || "");
  const [source, setSource] = useState<BookSource>(initial.source || "kendi");
  const [tests, setTests] = useState<BookTestDraft[]>(initial.tests?.length ? initial.tests : [{ title: "", questionCount: null }]);
  const [ortak, setOrtak] = useState("");
  const [yeniSatir, setYeniSatir] = useState<number | null>(null);
  const [yeniBolum, setYeniBolum] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Satır araya girince anahtarlar kayar, autoFocus çalışmaz: odağı yeni satıra elle veriyoruz
  useEffect(() => {
    if (yeniSatir === null) return;
    document.getElementById(`kitap-test-${yeniSatir}`)?.focus();
    setYeniSatir(null);
  }, [yeniSatir]);

  const degistir = (index: number, patch: Partial<BookTestDraft>) => setTests((current) => current.map((test, i) => (i === index ? { ...test, ...patch } : test)));

  const sil = (index: number) => {
    const test = tests[index];
    if ((test.solvedAt || test.wrongs?.length || test.planItem) && !window.confirm("Bu testin sonuçları, yanlış soruları ve plan kaydı da silinecek. Devam edilsin mi?")) return;
    setTests((current) => current.filter((_, i) => i !== index));
  };

  const ortakUygula = () => {
    const n = sayiYaDaBos(ortak);
    if (!n) return;
    setTests((current) => current.map((test) => (test.questionCount ? test : { ...test, questionCount: n })));
    setOrtak("");
  };

  const testEkle = (sira: number, topic: BookTestDraft["topic"]) => {
    setTests((current) => [...current.slice(0, sira), { title: "", questionCount: null, topic }, ...current.slice(sira)]);
    setYeniSatir(sira);
  };

  const bolumAdiDegistir = (bas: number, ad: string) => {
    setYeniBolum(null);
    setTests((current) => {
      let son = bas;
      while (current[son + 1] && ayniBolum(current[son + 1], current[bas])) son += 1;
      return current.map((test, i) => (i >= bas && i <= son ? { ...test, topic: ad } : test));
    });
  };

  const bolumEkle = () => {
    // Ad yandaki bölümle aynı olursa ikisi birleşir ve yeni bölüm hiç açılmamış görünür
    let numara = new Set(tests.map((test) => test.topic).filter(Boolean)).size + 1;
    while (tests.some((test) => test.topic === `Bölüm ${numara}`)) numara += 1;
    const ad = `Bölüm ${numara}`;
    const sonSira = tests.length - 1;
    const son = tests[sonSira];
    // Boş son satır yeni bölümün ilk testi olur, üstünde başlıksız boş satır kalmaz
    const sonBos = son && !son.id && !son.topic && !son.title.trim();
    setYeniBolum(sonBos ? sonSira : tests.length);
    if (sonBos) degistir(sonSira, { topic: ad });
    else setTests((current) => [...current, { title: "", questionCount: null, topic: ad }]);
  };

  const eksikSayi = tests.filter((test) => !test.questionCount).length;

  const kaydet = async () => {
    if (tests.some((test) => test.id && !test.title.trim())) { setError("Test adını boş bırakma. Testi silmek için çöp kutusunu kullan."); return; }
    const dolu = tests.filter((test) => test.title.trim());
    if (!title.trim()) { setError("Kitabın adını yaz."); return; }
    if (!dolu.length) { setError("En az bir test ekle."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave({ ...(initial.readId ? { readId: initial.readId } : {}), ...(initial.catalogId ? { catalogId: initial.catalogId } : {}), title: title.trim(), publisher: publisher.trim(), subject: subject.trim(), source, tests: dolu.map(({ id, title: ad, topic, questionCount }) => ({ ...(id ? { id } : {}), title: ad.trim(), topic: topic || null, questionCount: questionCount || null })) });
    } catch (err) {
      setError(hataMetni(err));
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Telefonda panel iç içe kayar, alttaki çubuk ekrana yapışmaz: Kaydet başta da durur */}
      <div className="flex items-center justify-between gap-2">
        <AdimBasligi baslik={baslik} onBack={onCancel} />
        <button type="button" onClick={kaydet} disabled={saving} className={`${BTN_ANA} shrink-0 sm:hidden`}>Kaydet</button>
      </div>
      {error && <p className="text-sm text-rose-700 sm:hidden">{error}</p>}
      <div className="grid gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-2">
        <label className="text-sm font-medium text-gray-700 sm:col-span-2">Kitabın adı
          <input id="kitap-ad" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} className={`${INPUT} mt-1`} />
        </label>
        <label className="text-sm font-medium text-gray-700">Yayınevi
          <input id="kitap-yayinevi" maxLength={120} value={publisher} onChange={(e) => setPublisher(e.target.value)} className={`${INPUT} mt-1`} />
        </label>
        <label className="text-sm font-medium text-gray-700">Ders
          <input id="kitap-ders" maxLength={80} value={subject} onChange={(e) => setSubject(e.target.value)} className={`${INPUT} mt-1`} />
        </label>
        <label className="text-sm font-medium text-gray-700 sm:col-span-2">Kitabı kim verdi?
          <select id="kitap-kaynak" value={source} onChange={(e) => setSource(e.target.value as BookSource)} className={`${INPUT} mt-1`}>
            {(Object.keys(KAYNAK_ADLARI) as BookSource[]).map((key) => <option key={key} value={key}>{KAYNAK_ADLARI[key]}</option>)}
          </select>
        </label>
      </div>

      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Testler ({tests.length})</h3>
        {eksikSayi > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-blue-50 p-2.5 text-sm text-blue-950">
            <label htmlFor="kitap-ortak-sayi">{eksikSayi} testin soru sayısı eksik. Her test kaç soru?</label>
            <input id="kitap-ortak-sayi" inputMode="numeric" value={ortak} onChange={(e) => setOrtak(e.target.value.replace(/\D/g, ""))} className="w-16 rounded-md border border-blue-200 bg-white px-2 py-1 text-sm" />
            <button type="button" onClick={ortakUygula} disabled={!sayiYaDaBos(ortak)} className="rounded-md bg-blue-600 px-2.5 py-1 text-sm font-medium text-white disabled:opacity-50">Uygula</button>
          </div>
        )}
        <ul className="space-y-2">
          {tests.map((test, index) => {
            const bolumBasi = index === 0 || !ayniBolum(tests[index - 1], test);
            const bolumSonu = index === tests.length - 1 || !ayniBolum(tests[index + 1], test);
            return (
              <Fragment key={test.id || index}>
                {bolumBasi && (
                  <li className="pt-3 first:pt-0">
                    <BolumBasligi key={test.topic || ""} ad={test.topic || ""} odakla={index === yeniBolum} onDegis={(ad) => bolumAdiDegistir(index, ad)} />
                  </li>
                )}
                <li className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2 sm:flex-nowrap sm:border-0 sm:p-0">
                  <div className="relative w-full min-w-0 sm:flex-1">
                    <input id={`kitap-test-${index}`} aria-label={`${index + 1}. test adı`} placeholder="Test adı" maxLength={200} value={test.title} onChange={(e) => degistir(index, { title: e.target.value })} className={`${INPUT} ${test.solvedAt ? "pr-9" : ""}`} />
                    {test.solvedAt && <Check aria-label="Sonucu girildi" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />}
                  </div>
                  <label className="flex shrink-0 items-center gap-2 text-sm text-gray-600">
                    <span className="sm:sr-only">Soru</span>
                    <input aria-label={`${index + 1}. test soru sayısı`} placeholder="Soru" inputMode="numeric" value={test.questionCount ?? ""} onChange={(e) => degistir(index, { questionCount: sayiYaDaBos(e.target.value.replace(/\D/g, "")) })} className={`w-20 text-center ${INPUT_TABAN}`} />
                  </label>
                  <button type="button" aria-label={`${index + 1}. testi sil`} onClick={() => sil(index)} className="ml-auto shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-rose-600 sm:ml-0"><Trash2 className="h-4 w-4" /></button>
                </li>
                {bolumSonu && (
                  <li>
                    <button type="button" onClick={() => testEkle(index + 1, test.topic)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"><Plus className="h-4 w-4" /> Test ekle</button>
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>
        <button type="button" onClick={bolumEkle} className={`${BTN_IKINCIL} w-full`}><Plus className="h-4 w-4" /> Bölüm ekle</button>
      </div>

      {/* Uzun listede Kaydet için sona kaydırmak gerekmesin */}
      <div className="sticky bottom-0 z-10 -mx-3 space-y-2 border-t border-gray-200 bg-gray-50 px-3 py-3 sm:-mx-6 sm:px-6">
        {error && <p className="text-sm text-rose-700">{error}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} disabled={saving} className={BTN_IKINCIL}>Vazgeç</button>
          <button type="button" onClick={kaydet} disabled={saving} className={`${BTN_ANA} flex-1`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

function SonucFormu({ test, kayit, onSave, onCancel }: { test: StudentBookTest; kayit: StudentBookResult | null; onSave: (body: BookResultBody) => Promise<void>; onCancel: () => void }) {
  const [solvedOn, setSolvedOn] = useState(kayit?.solvedOn || bugunAnahtari());
  const [correct, setCorrect] = useState(kayit?.correct.toString() ?? "");
  const [wrong, setWrong] = useState(kayit?.wrong.toString() ?? "");
  const [blank, setBlank] = useState(kayit?.blank.toString() ?? "");
  const [minutes, setMinutes] = useState(kayit?.minutes?.toString() ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sayi = (value: string) => Number(value) || 0;
  const toplam = sayi(correct) + sayi(wrong) + sayi(blank);

  const kaydet = async () => {
    setBusy(true);
    setError("");
    try {
      await onSave({ solvedOn, correct: sayi(correct), wrong: sayi(wrong), blank: sayi(blank), minutes: Math.min(sayi(minutes), MAX_DAKIKA) || null });
    } catch (err) {
      setError(hataMetni(err));
      setBusy(false);
    }
  };

  const alan = (ad: string, id: string, value: string, set: (v: string) => void) => (
    <label className="text-xs font-medium text-gray-600">{ad}
      <input id={id} inputMode="numeric" value={value} onChange={(e) => set(e.target.value.replace(/\D/g, ""))} className={`${INPUT} mt-1`} />
    </label>
  );

  return (
    <div className="space-y-3">
      <label className="block text-xs font-medium text-gray-600">Çözdüğün gün
        <input id={`tarih-${test.id}`} type="date" max={bugunAnahtari()} value={solvedOn} onChange={(e) => setSolvedOn(e.target.value)} className={`${INPUT} mt-1`} />
      </label>
      <div className="grid grid-cols-4 gap-2">
        {alan("Doğru", `dogru-${test.id}`, correct, setCorrect)}
        {alan("Yanlış", `yanlis-${test.id}`, wrong, setWrong)}
        {alan("Boş", `bos-${test.id}`, blank, setBlank)}
        {alan("Dakika", `dakika-${test.id}`, minutes, setMinutes)}
      </div>
      <p className="text-xs text-gray-500">Toplam {toplam}{test.questionCount ? ` / ${test.questionCount} soru` : " soru"}. Cevapları kitabın anahtarından kontrol et.</p>
      {error && <p className="text-sm text-rose-700">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || toplam === 0 || !solvedOn} onClick={kaydet} className={BTN_ANA}>{kayit ? "Kaydı güncelle" : "Kaydet"}</button>
        <button type="button" disabled={busy} onClick={onCancel} className={BTN_IKINCIL}>Vazgeç</button>
      </div>
    </div>
  );
}

function PlanSatiri({ book, test, onUpdated }: { book: StudentBook; test: StudentBookTest; onUpdated: (data: StudentBook) => void }) {
  const [gun, setGun] = useState(() => (test.planItem && test.planItem.scheduledDate >= bugunAnahtari() ? test.planItem.scheduledDate : bugunAnahtari()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const calistir = async (is: () => Promise<StudentBook>) => {
    setBusy(true);
    setError("");
    try {
      onUpdated(await is());
    } catch (err) {
      setError(hataMetni(err));
    } finally {
      setBusy(false);
    }
  };

  const kacirildi = test.planItem?.status === "MISSED";
  return (
    <div className="space-y-2 rounded-lg bg-gray-50 p-2.5">
      <p className="text-xs font-medium text-gray-600">
        {test.planItem ? `Koçum planında: ${gunYazisi(test.planItem.scheduledDate)}${kacirildi ? " (kaçırıldı)" : ""}` : "Koçum planına ekle"}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input id={`plan-${test.id}`} aria-label="Plan günü" type="date" min={bugunAnahtari()} value={gun} onChange={(e) => setGun(e.target.value)} className={`${INPUT_TABAN} min-w-0`} />
        <button type="button" disabled={busy || !gun} onClick={() => calistir(() => ApiService.planBookTest(book.id, test.id, gun))} className={BTN_IKINCIL}>
          <CalendarPlus className="h-4 w-4" /> {test.planItem ? "Günü değiştir" : "Plana ekle"}
        </button>
        {test.planItem && <button type="button" disabled={busy} onClick={() => calistir(() => ApiService.unplanBookTest(book.id, test.id))} className="text-sm font-medium text-rose-700 hover:underline disabled:opacity-50">Plandan çıkar</button>}
      </div>
      {error && <p className="text-sm text-rose-700">{error}</p>}
    </div>
  );
}

function SoruFotografi({ tekrar }: { tekrar: StudentBookWrong["review"] }) {
  const [acik, setAcik] = useState(false);
  // Düzelen sorunun fotoğrafı silinir, açılacak bir şey kalmaz
  if (tekrar?.status !== "AKTIF") return null;
  return (
    <div>
      <button type="button" onClick={() => setAcik((deger) => !deger)} className="text-xs font-medium text-blue-600 hover:underline">{acik ? "Soruyu gizle" : "Soruyu gör"}</button>
      {acik && <div className="mt-2"><TekrarFotografi id={tekrar.id} /></div>}
    </div>
  );
}

function YanlisSatiri({ book, yanlis, onUpdated }: { book: StudentBook; yanlis: StudentBookWrong; onUpdated: (data: StudentBook) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const calistir = async (is: () => Promise<StudentBook>) => {
    setBusy(true);
    setError("");
    try {
      onUpdated(await is());
    } catch (err) {
      setError(hataMetni(err));
    } finally {
      setBusy(false);
    }
  };

  const eslesti = yanlis.status === "ESLESTI" || yanlis.status === "TAVAN";
  const baslik = yanlis.questionNo ? `${yanlis.questionNo}. soru` : "Soru";
  if (yanlis.status === "SORU") {
    return (
      <li className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-gray-800">
        <p className="font-medium">{baslik}: hangisine daha yakın?</p>
        <SoruFotografi tekrar={yanlis.review} />
        <div className="space-y-1.5">
          {(yanlis.options || []).map((secenek) => (
            <button key={secenek.gainId} type="button" disabled={busy} onClick={() => calistir(() => ApiService.confirmBookWrong(book.id, yanlis.id, secenek.gainId))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-blue-400 disabled:opacity-50">{secenek.text}</button>
          ))}
          <button type="button" disabled={busy} onClick={() => calistir(() => ApiService.confirmBookWrong(book.id, yanlis.id, null))} className="text-sm font-medium text-gray-600 hover:underline disabled:opacity-50">Hiçbiri</button>
        </div>
        {error && <p className="text-rose-700">{error}</p>}
      </li>
    );
  }
  return (
    <li className="flex items-start gap-3 text-sm text-gray-800">
      <div className="min-w-0 flex-1">
        <p><span className="font-medium">{baslik}:</span> {eslesti ? yanlis.gainText : "Kazanımla eşleşmedi."}</p>
        {eslesti && <p className="text-xs text-gray-500">{yanlis.status === "TAVAN" ? "Açık eksiğin çok, bu eklenmedi. Önce eksiklerini kapat." : yanlis.userGainId ? "Eksiklerine eklendi" : "Bu kazanım zaten eksiklerinde"}</p>}
        {yanlis.review && <p className="text-xs font-medium text-teal-700">{yanlis.review.status === "DUZELDI" ? "Düzeldi" : `Tekrar günü: ${gunYazisi(yanlis.review.nextOn)}`}</p>}
        <SoruFotografi tekrar={yanlis.review} />
        {error && <p className="text-xs text-rose-700">{error}</p>}
      </div>
      <button type="button" disabled={busy} onClick={() => calistir(() => ApiService.deleteBookWrong(book.id, yanlis.id))} className="shrink-0 text-xs font-medium text-rose-700 hover:underline disabled:opacity-50">Kaldır</button>
    </li>
  );
}

function YanlisSorular({ book, test, yukleyebilir, onUpdated }: { book: StudentBook; test: StudentBookTest; yukleyebilir: boolean; onUpdated: (data: StudentBook) => void }) {
  const [numara, setNumara] = useState("");
  const [hataTuru, setHataTuru] = useState<BookErrorType | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const yukle = async (list: FileList | null) => {
    const dosya = list?.[0];
    if (!dosya) return;
    if (/hei[cf]/i.test(dosya.type) || /\.hei[cf]$/i.test(dosya.name)) {
      setError("HEIC biçimi açılamıyor. JPEG olarak yükle ya da doğrudan kamerayla çek.");
      return;
    }
    if (dosya.size > MAX_FOTO_MB * 1024 * 1024) {
      setError(`Fotoğraf en çok ${MAX_FOTO_MB} MB olabilir.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      onUpdated(await ApiService.addBookWrong(book.id, test.id, [dosya], sayiYaDaBos(numara), hataTuru || null));
      setNumara("");
      setHataTuru("");
    } catch (err) {
      setError(hataMetni(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg bg-gray-50 p-2.5">
      <p className="text-xs font-medium text-gray-600">Yanlış soruların</p>
      {test.wrongs.length > 0 && (
        <ul className="space-y-2">
          {test.wrongs.map((yanlis) => <YanlisSatiri key={yanlis.id} book={book} yanlis={yanlis} onUpdated={onUpdated} />)}
        </ul>
      )}
      {yukleyebilir && (
        <>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Hata türü" value={hataTuru} onChange={(e) => setHataTuru(e.target.value as BookErrorType | "")} className={INPUT_TABAN}>
          <option value="">Neden yanlış?</option>
          <option value="BILGI">Bilmiyordum</option>
          <option value="DIKKAT">Dikkatsizlik</option>
          <option value="SURE">Süre yetmedi</option>
        </select>
        <input aria-label="Soru numarası" placeholder="Soru no" inputMode="numeric" value={numara} onChange={(e) => setNumara(e.target.value.replace(/\D/g, ""))} className={`w-20 text-center ${INPUT_TABAN}`} />
        <label htmlFor={`yanlis-kamera-${test.id}`} className={`${BTN_IKINCIL} cursor-pointer`}><Camera className="h-4 w-4" /> Fotoğraf çek</label>
        <label htmlFor={`yanlis-galeri-${test.id}`} className={`${BTN_IKINCIL} cursor-pointer`}><ImagePlus className="h-4 w-4" /> Galeriden</label>
        <input id={`yanlis-kamera-${test.id}`} type="file" accept="image/*" capture="environment" disabled={busy} className="hidden" onChange={(e) => { yukle(e.target.files); e.target.value = ""; }} />
        <input id={`yanlis-galeri-${test.id}`} type="file" accept="image/*" disabled={busy} className="hidden" onChange={(e) => { yukle(e.target.files); e.target.value = ""; }} />
      </div>
      {busy ? <p className="flex items-center gap-1.5 text-sm text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> Soru eşleştiriliyor</p> : <p className="text-xs text-gray-500">Soru kazanımla eşleşir, eksiklerine eklenir. Fotoğraf tekrar için yalnız sende saklanır, kaldırınca silinir.</p>}
      {error && <p className="text-sm text-rose-700">{error}</p>}
        </>
      )}
    </div>
  );
}

function TestKayitlari({ book, test, planAcik, onUpdated, onClose }: { book: StudentBook; test: StudentBookTest; planAcik: boolean; onUpdated: (data: StudentBook) => void; onClose: () => void }) {
  const [duzenlenen, setDuzenlenen] = useState<StudentBookResult | null>(null);
  const [yeni, setYeni] = useState(!test.results.length);
  const [error, setError] = useState("");

  const sil = async (kayit: StudentBookResult) => {
    if (!window.confirm(`${gunYazisi(kayit.solvedOn)} kaydı silinecek. Devam edilsin mi?`)) return;
    try {
      onUpdated(await ApiService.deleteBookResult(book.id, test.id, kayit.id));
    } catch (err) {
      setError(hataMetni(err));
    }
  };

  const formAcik = yeni || duzenlenen;
  return (
    <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
      {planAcik && book.hasPlan && <PlanSatiri key={test.planItem?.scheduledDate || "yeni"} book={book} test={test} onUpdated={onUpdated} />}
      {test.results.length > 0 && (
        <ul className="space-y-1.5">
          {test.results.map((kayit) => (
            <li key={kayit.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
              <span className="font-medium">{gunYazisi(kayit.solvedOn)}</span>
              <span className="text-gray-600">{kayit.correct} D · {kayit.wrong} Y · {kayit.blank} B{kayit.minutes ? ` · ${kayit.minutes} dk` : ""}</span>
              <span className="ml-auto flex gap-3 text-xs font-medium">
                <button type="button" onClick={() => { setDuzenlenen(kayit); setYeni(false); }} className="text-blue-600 hover:underline">Düzelt</button>
                <button type="button" onClick={() => sil(kayit)} className="text-rose-700 hover:underline">Sil</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {((planAcik && book.hasPlan) || test.wrongs.length > 0) && <YanlisSorular book={book} test={test} yukleyebilir={planAcik && book.hasPlan} onUpdated={onUpdated} />}
      {error && <p className="text-sm text-rose-700">{error}</p>}
      {formAcik ? (
        <SonucFormu
          key={duzenlenen?.id || "yeni"}
          test={test}
          kayit={duzenlenen}
          onCancel={() => {
            if (!test.results.length) onClose();
            setDuzenlenen(null);
            setYeni(false);
          }}
          onSave={async (body) => {
            onUpdated(await (duzenlenen ? ApiService.updateBookResult(book.id, test.id, duzenlenen.id, body) : ApiService.addBookResult(book.id, test.id, body)));
            setDuzenlenen(null);
            setYeni(false);
          }}
        />
      ) : (
        <button type="button" onClick={() => setYeni(true)} className={BTN_IKINCIL}><Plus className="h-4 w-4" /> Yeniden çözdüm</button>
      )}
    </div>
  );
}

function KitapDetayi({ bookId, testId, planAcik, onBack, onChanged }: { bookId: string; testId: string | null; planAcik: boolean; onBack: () => void; onChanged: () => void }) {
  const [book, setBook] = useState<StudentBook | null>(null);
  const [acik, setAcik] = useState<string | null>(testId);
  const [duzenle, setDuzenle] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    ApiService.getBook(bookId).then((data) => { if (alive) setBook(data); }).catch((err) => { if (alive) setError(hataMetni(err)); });
    return () => { alive = false; };
  }, [bookId]);

  const guncelle = (data: StudentBook) => {
    setBook(data);
    onChanged();
  };

  const kaldir = async () => {
    if (!book || !window.confirm(`"${book.title}" ve test sonuçları kaldırılacak. Devam edilsin mi?`)) return;
    try {
      await ApiService.deleteBook(book.id);
      onChanged();
      onBack();
    } catch (err) {
      setError(hataMetni(err));
    }
  };

  if (error) return <div className="space-y-3"><AdimBasligi baslik="Kitap" onBack={onBack} /><p className="text-sm text-rose-700">{error}</p></div>;
  if (!book) return <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>;

  if (duzenle) {
    return (
      <KitapFormu
        baslik="Kitabı düzenle"
        initial={{ title: book.title, publisher: book.publisher || "", subject: book.subject || "", source: book.source, tests: book.tests }}
        onCancel={() => setDuzenle(false)}
        onSave={async (payload) => {
          guncelle(await ApiService.updateBook(book.id, payload));
          setDuzenle(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <AdimBasligi baslik={book.title} onBack={onBack} />
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {(book.publisher || book.subject) && <p className="mb-2 text-sm text-gray-500">{[book.publisher, book.subject].filter(Boolean).join(" · ")}</p>}
        <Ilerleme book={book} />
        {book.solvedQuestions > 0 && <p className="mt-2 text-sm text-gray-700">{book.correct} doğru · {book.wrong} yanlış · {book.blank} boş</p>}
      </div>

      <ul className="space-y-2">
        {book.tests.map((test, index) => (
          <Fragment key={test.id}>
          {konuBasligi(book.tests, index) && <li className="pt-2 text-xs font-semibold text-gray-500 first:pt-0">{konuBasligi(book.tests, index)}</li>}
          <li className="rounded-xl border border-gray-200 bg-white p-3">
            <button type="button" onClick={() => setAcik(acik === test.id ? null : test.id)} className="flex w-full items-center gap-3 text-left">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border ${test.solvedAt ? "border-emerald-600 bg-emerald-600 text-white" : "border-gray-300 text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">{test.title}</span>
                <span className="block text-xs text-gray-500">
                  {test.results[0]
                    ? `${gunYazisi(test.results[0].solvedOn)}: ${test.correct} doğru · ${test.wrong} yanlış · ${test.blank} boş${test.results.length > 1 ? ` · ${test.results.length} kayıt` : ""}`
                    : test.questionCount ? `${test.questionCount} soru` : "Sonuç girilmedi"}
                </span>
                {test.planItem && <span className="block text-xs font-medium text-teal-700">Planda: {gunYazisi(test.planItem.scheduledDate)}</span>}
              </span>
            </button>
            {acik === test.id && <TestKayitlari book={book} test={test} planAcik={planAcik} onUpdated={guncelle} onClose={() => setAcik(null)} />}
          </li>
          </Fragment>
        ))}
      </ul>

      <div className="flex flex-wrap justify-between gap-2">
        <button type="button" onClick={() => setDuzenle(true)} className={BTN_IKINCIL}><PenLine className="h-4 w-4" /> Testleri düzenle</button>
        <button type="button" onClick={kaldir} className="text-sm font-medium text-rose-700 hover:underline">Kitabı kaldır</button>
      </div>
    </div>
  );
}

function TekrarFotografi({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [hata, setHata] = useState(false);

  useEffect(() => {
    let alive = true;
    let adres: string | null = null;
    setUrl(null);
    setHata(false);
    ApiService.getBookReviewPhoto(id)
      .then((blob) => {
        if (!alive) return;
        adres = URL.createObjectURL(blob);
        setUrl(adres);
      })
      .catch(() => {
        if (alive) setHata(true);
      });
    return () => {
      alive = false;
      if (adres) URL.revokeObjectURL(adres);
    };
  }, [id]);

  if (hata) return <p className="py-8 text-center text-sm text-gray-500">Fotoğraf açılamadı. Soruyu kitabından bulup çözebilirsin.</p>;
  if (!url) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  return <img src={url} alt="Yanlış yaptığın soru" className="mx-auto block max-h-[60vh] w-auto max-w-full rounded-md" />;
}

function TekrarEkrani({ onBack, onChanged }: { onBack: () => void; onChanged: () => void }) {
  const [veri, setVeri] = useState<BookReviews | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    ApiService.getBookReviews().then((data) => { if (alive) setVeri(data); }).catch((err) => { if (alive) setError(hataMetni(err)); });
    return () => { alive = false; };
  }, []);

  const cevapla = async (item: BookReviewItem, dogru: boolean) => {
    setBusy(true);
    setError("");
    try {
      setVeri(await ApiService.answerBookReview(item.id, dogru));
      onChanged();
    } catch (err) {
      setError(hataMetni(err));
      // Yanıt kaybolduysa ya da başka cihazda cevaplandıysa aynı soruda takılı kalmasın
      const guncel = await ApiService.getBookReviews().catch(() => null);
      if (guncel) setVeri(guncel);
    } finally {
      setBusy(false);
    }
  };

  const item = veri?.items[0];
  return (
    <div className="space-y-4">
      <AdimBasligi baslik="Bugünkü tekrarlar" onBack={onBack} />
      {!veri && !error && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>}
      {veri && !item && (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <Check className="mx-auto h-8 w-8 text-emerald-600" />
          <p className="mt-2 text-sm font-medium text-gray-900">Bugünkü tekrarların bitti</p>
          <p className="mt-1 text-sm text-gray-500">{veri.due > 0 ? `Günde ${veri.limit} soru tekrar edilir, kalan ${veri.due} soru sonraki günlere kaldı.` : "Tekrar günü gelen başka soru yok."}</p>
        </div>
      )}
      {item && (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">{[item.bookTitle, item.testTitle, item.questionNo ? `${item.questionNo}. soru` : null].filter(Boolean).join(" · ")}</p>
          <TekrarFotografi id={item.id} />
          {item.gainText && <p className="text-xs text-gray-600"><span className="font-medium">Kazanım:</span> {item.gainText}</p>}
          <p className="text-sm text-gray-700">Soruyu yeniden çöz, cevabını kitabın anahtarından kontrol et.</p>
          {error && <p className="text-sm text-rose-700">{error}</p>}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={busy} onClick={() => cevapla(item, false)} className={BTN_IKINCIL}>Yine yanlış</button>
            <button type="button" disabled={busy} onClick={() => cevapla(item, true)} className={BTN_ANA}>Doğru yaptım</button>
          </div>
          <p className="text-center text-xs text-gray-500">Bugün {veri?.doneToday} / {veri?.limit}. Art arda 2 doğruda soru düzelmiş sayılır.</p>
        </div>
      )}
      {!veri && error && <p className="text-sm text-rose-700">{error}</p>}
    </div>
  );
}

type Ekran = { ad: "liste" } | { ad: "tekrar" } | { ad: "foto" } | { ad: "form"; taslak: BookPayload } | { ad: "detay"; id: string };

export default function Kitaplarim({ planAcik = false }: { planAcik?: boolean }) {
  // Koçum Bugün'deki kitap kalemi doğrudan kitabı açar
  const parametreler = new URLSearchParams(useLocation().search);
  const kitapParam = parametreler.get("kitap");
  const testParam = parametreler.get("test");
  const [books, setBooks] = useState<StudentBookSummary[] | null>(null);
  const [quota, setQuota] = useState<BookReadQuota | null>(null);
  const [daily, setDaily] = useState<BookDaily[]>([]);
  const [tekrar, setTekrar] = useState<BookReviews | null>(null);
  const [ekran, setEkran] = useState<Ekran>(() => {
    if (planAcik && parametreler.get("tekrar")) return { ad: "tekrar" };
    if (kitapParam) return { ad: "detay", id: kitapParam };
    const taslak = taslakOku();
    return taslak ? { ad: "form", taslak } : { ad: "liste" };
  });
  const [error, setError] = useState("");

  const yukle = useCallback(async () => {
    try {
      const data = await ApiService.getBooks();
      setBooks(data.books);
      setDaily(data.daily);
      setQuota(data.quota);
      setError("");
      if (planAcik) setTekrar(await ApiService.getBookReviews().catch(() => null));
    } catch (err) {
      setError(hataMetni(err));
    }
  }, [planAcik]);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const icerik = () => {
    if (ekran.ad === "tekrar") return <TekrarEkrani onBack={() => setEkran({ ad: "liste" })} onChanged={yukle} />;
    if (ekran.ad === "foto") {
      return <FotoAdimi quota={quota} onCancel={() => setEkran({ ad: "liste" })} onManual={() => setEkran({ ad: "form", taslak: { title: "" } })} onDone={yukle} onRead={(taslak) => { taslakYaz(taslak); setEkran({ ad: "form", taslak }); }} />;
    }
    if (ekran.ad === "form") {
      return (
        <KitapFormu
          baslik="Testleri kontrol et"
          initial={ekran.taslak}
          onCancel={() => {
            if (ekran.taslak.tests?.length && !window.confirm("Okunan test listesi silinecek. Devam edilsin mi?")) return;
            taslakYaz(null);
            setEkran({ ad: "liste" });
          }}
          onSave={async (payload) => {
            const book = await ApiService.createBook(payload);
            taslakYaz(null);
            await yukle();
            setEkran({ ad: "detay", id: book.id });
          }}
        />
      );
    }
    if (ekran.ad === "detay") return <KitapDetayi bookId={ekran.id} testId={ekran.id === kitapParam ? testParam : null} planAcik={planAcik} onBack={() => setEkran({ ad: "liste" })} onChanged={yukle} />;
    if (error) {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
          <p>{error}</p>
          <button type="button" onClick={yukle} className="mt-2 font-medium text-blue-600 hover:underline">Yeniden dene</button>
        </div>
      );
    }
    if (!books) return <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>;
    return <KitapListesi books={books} daily={daily} tekrar={tekrar} onTekrar={() => setEkran({ ad: "tekrar" })} onOpen={(id) => setEkran({ ad: "detay", id })} onAdd={() => setEkran({ ad: "foto" })} />;
  };

  return <div className="mx-auto w-full max-w-3xl p-3 pb-6 sm:p-6">{icerik()}</div>;
}
